import { PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import type { ReplenishFlowType, ReplenishRow, ReplenishStatus } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = ReplenishStatus | 'ALL';

const STATUS_META: Record<ReplenishStatus, { color: string; text: string }> = {
  PENDING: { color: 'blue', text: '待采纳' },
  ADOPTED: { color: 'green', text: '已采纳' },
  IGNORED: { color: 'default', text: '已忽略' }
};

interface AdoptFormValues {
  suggestQty: number;
  flowType: ReplenishFlowType;
  remark?: string;
}

/**
 * 补货建议（运营端）：按库存规则扫描生成建议，采纳后直接落请购单（QG），
 * 形成「缺货预测 → 补货建议 → 请购」闭环。
 */
export default function ShortageReplenish() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('PENDING');
  const [current, setCurrent] = useState<ReplenishRow | null>(null);
  const [form] = Form.useForm<AdoptFormValues>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'replenish', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.replenish.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        status: status === 'ALL' ? '' : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const generate = useMutation({
    mutationFn: () => hq.replenish.generate(),
    onSuccess: async (result) => {
      message.success(
        `扫描 ${result.scanned} 个仓位物资，新增 ${result.created} 条、刷新 ${result.refreshed} 条建议`
      );
      await queryClient.invalidateQueries({ queryKey: ['ops', 'replenish'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '生成补货建议失败');
    }
  });

  const adopt = useMutation({
    mutationFn: (values: AdoptFormValues) => {
      if (!current) throw new Error('未选择建议');
      return hq.replenish.adopt(current.id, {
        suggestQty: values.suggestQty,
        flowType: values.flowType,
        remark: values.remark
      });
    },
    onSuccess: async (result) => {
      message.success(`已生成请购单 ${result.requireBizNo}`);
      setCurrent(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'replenish'] });
      await queryClient.invalidateQueries({ queryKey: ['ops', 'require'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '采纳失败，请稍后重试');
    }
  });

  const ignore = useMutation({
    mutationFn: (id: string) => hq.replenish.ignore(id),
    onSuccess: async () => {
      message.success('已忽略该建议');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'replenish'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '忽略失败，请稍后重试');
    }
  });

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const error = list.error instanceof ApiError ? list.error.friendlyMessage : null;

  const columns: ColumnsType<ReplenishRow> = [
    {
      title: '物资',
      ellipsis: { showTitle: false },
      // 单行展示：编码收进悬浮提示，避免行高被拉高
      render: (_, row) => <TipText text={row.materialName ?? '-'} tip={`${row.materialName ?? '-'} · ${row.materialCode ?? '-'}`} />
    },
    { title: '仓库', dataIndex: 'warehouseId', width: 90, render: (value: string) => `#${value}` },
    {
      title: '当前库存',
      dataIndex: 'currentQty',
      width: 100,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '安全库存',
      dataIndex: 'safetyStock',
      width: 100,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '在途',
      dataIndex: 'onwayQty',
      width: 90,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '建议补货量',
      dataIndex: 'suggestQty',
      width: 110,
      align: 'right',
      render: (value: number) => <Typography.Text strong>{formatQty(value)}</Typography.Text>
    },
    { title: '建议到货', dataIndex: 'suggestAt', width: 120, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '依据',
      dataIndex: 'reason',
      ellipsis: { showTitle: false },
      render: (value: string | null) => (value ? <TipText text={value} /> : '-')
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: ReplenishStatus) => {
        const meta = STATUS_META[value] ?? { color: 'default', text: value };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '请购单',
      dataIndex: 'requireBizNo',
      width: 200,
      render: (value: string | null) => value || '-'
    },
    {
      title: '操作',
      width: 140,
      fixed: 'right',
      render: (_, row) =>
        row.status === 'PENDING' ? (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setCurrent(row);
                form.setFieldsValue({ suggestQty: row.suggestQty, flowType: 'TEMP' });
              }}
            >
              采纳
            </Button>
            <Popconfirm
              title="忽略该补货建议？"
              okText="忽略"
              cancelText="取消"
              onConfirm={() => ignore.mutate(row.id)}
            >
              <Button type="link" size="small" danger loading={ignore.isPending}>
                忽略
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        )
    }
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented<StatusFilter>
          value={status}
          options={[
            { label: '待采纳', value: 'PENDING' },
            { label: '已采纳', value: 'ADOPTED' },
            { label: '已忽略', value: 'IGNORED' },
            { label: '全部', value: 'ALL' }
          ]}
          onChange={(value) => setStatus(value)}
        />
        <Button icon={<PlusOutlined />} loading={generate.isPending} onClick={() => generate.mutate()}>
          生成建议
        </Button>
        <Button icon={<SettingOutlined />} onClick={() => navigate('/purchase/stock-rule')}>
          库存规则
        </Button>
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
      </Space>

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="加载失败" description={error} /> : null}

      <Table<ReplenishRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无补货建议，可点击「生成建议」按库存规则扫描' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={`采纳补货建议 ${current?.materialName ?? ''}`}
        open={current !== null}
        onCancel={() => setCurrent(null)}
        onOk={() => form.submit()}
        okText="生成请购单"
        confirmLoading={adopt.isPending}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`当前库存 ${formatQty(current?.currentQty ?? 0)} · 安全库存 ${formatQty(
            current?.safetyStock ?? 0
          )} · 在途 ${formatQty(current?.onwayQty ?? 0)}`}
          description={current?.reason ?? undefined}
        />
        <Form<AdoptFormValues> form={form} layout="vertical" onFinish={(values) => adopt.mutate(values)}>
          <Form.Item
            name="suggestQty"
            label="请购数量"
            rules={[{ required: true, message: '请填写请购数量' }]}
          >
            <InputNumber min={0.001} precision={3} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="flowType" label="请购类型" rules={[{ required: true, message: '请选择请购类型' }]}>
            <Select
              options={[
                { value: 'TEMP', label: '临时请购' },
                { value: 'MONTH_PLAN', label: '月度计划' },
                { value: 'EMERGENCY', label: '应急请购' }
              ]}
            />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
